import React, { useState, useEffect } from 'react';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './MergeStudios.css';

export default function MergeStudios() {
  const [loading, setLoading] = useState(false);
  const [allStudios, setAllStudios] = useState([]);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [selectedStudios, setSelectedStudios] = useState([]);
  const [mergeStatus, setMergeStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const [user, setUser] = useState(null);

  // Auth state management
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadAllStudios();
  }, []);

  // 🧹 Helper function to normalize studio names for comparison
  function normalizeStudioName(name) {
    if (!name) return "";
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/\b(dance\s*)?(studio|school|academy|center|centre)\b/gi, '') // Remove common studio words
      .trim();
  }

  // 📏 Calculate similarity between two studio names using Levenshtein distance
  function calculateSimilarity(name1, name2) {
    const str1 = normalizeStudioName(name1);
    const str2 = normalizeStudioName(name2);
    
    if (str1 === str2) return 100;
    if (str1.length === 0 || str2.length === 0) return 0;
    
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const substitutionCost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + substitutionCost // substitution
        );
      }
    }
    
    const maxLength = Math.max(str1.length, str2.length);
    const distance = matrix[str2.length][str1.length];
    return Math.round(((maxLength - distance) / maxLength) * 100);
  }

  // 🔍 Check if two studio names are similar enough to be considered duplicates
  function areStudiosSimilar(name1, name2, threshold = 70) {
    if (!name1 || !name2) return false;
    
    const norm1 = normalizeStudioName(name1);
    const norm2 = normalizeStudioName(name2);
    
    // Exact match after normalization
    if (norm1 === norm2) return true;
    
    // Check if the words are the same but in different order
    const words1 = norm1.split(" ");
    const words2 = norm2.split(" ");
    
    if (words1.length === words2.length && words1.every(word => words2.includes(word))) {
      return true;
    }
    
    // Fuzzy matching with similarity threshold
    const similarity = calculateSimilarity(name1, name2);
    return similarity >= threshold;
  }

  // 📊 Load all unique studios from all competitions
  async function loadAllStudios() {
    setLoading(true);
    setMergeStatus("🔍 Loading all studios from competitions...");
    
    const studiosMap = new Map();
    
    try {
      // Query old structure
      const oldQuery = collection(db, "competitions");
      const oldSnapshot = await getDocs(oldQuery);
      
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (comp.results) {
          comp.results.forEach((result) => {
            const studioName = result.studio || "Unknown";
            const dancerName = result.dancerName;
            const compId = doc.id;
            const compName = comp.name || compId;
            
            if (studioName && studioName !== "Unknown") {
              const key = studioName; // Keep original name as key
              
              if (!studiosMap.has(key)) {
                studiosMap.set(key, {
                  name: studioName,
                  dancers: new Set(),
                  competitions: new Set(),
                  totalResults: 0,
                  sampleCompetitions: []
                });
              }
              
              const studio = studiosMap.get(key);
              if (dancerName) studio.dancers.add(dancerName);
              studio.competitions.add(compName);
              studio.totalResults++;
              
              if (studio.sampleCompetitions.length < 3) {
                studio.sampleCompetitions.push(compName);
              }
            }
          });
        }
      });
      
      // Query new structure
      try {
        const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
        
        for (const disciplineDoc of disciplinesSnapshot.docs) {
          const disciplineName = disciplineDoc.id;
          const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
          
          for (const categoryDoc of categoriesSnapshot.docs) {
            const categoryName = categoryDoc.id;
            
            // Check category competitions
            try {
              const categoryCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
              
              categoryCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                const compName = comp.name || doc.id;
                
                if (comp.results) {
                  comp.results.forEach((result) => {
                    const studioName = result.studio || "Unknown";
                    const dancerName = result.dancerName;
                    
                    if (studioName && studioName !== "Unknown") {
                      const key = studioName;
                      
                      if (!studiosMap.has(key)) {
                        studiosMap.set(key, {
                          name: studioName,
                          dancers: new Set(),
                          competitions: new Set(),
                          totalResults: 0,
                          sampleCompetitions: []
                        });
                      }
                      
                      const studio = studiosMap.get(key);
                      if (dancerName) studio.dancers.add(dancerName);
                      studio.competitions.add(compName);
                      studio.totalResults++;
                      
                      if (studio.sampleCompetitions.length < 3) {
                        studio.sampleCompetitions.push(compName);
                      }
                    }
                  });
                }
              });
            } catch (error) {
              // No direct competitions
            }
            
            // Check dance-specific competitions
            try {
              const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  const compName = comp.name || doc.id;
                  
                  if (comp.results) {
                    comp.results.forEach((result) => {
                      const studioName = result.studio || "Unknown";
                      const dancerName = result.dancerName;
                      
                      if (studioName && studioName !== "Unknown") {
                        const key = studioName;
                        
                        if (!studiosMap.has(key)) {
                          studiosMap.set(key, {
                            name: studioName,
                            dancers: new Set(),
                            competitions: new Set(),
                            totalResults: 0,
                            sampleCompetitions: []
                          });
                        }
                        
                        const studio = studiosMap.get(key);
                        if (dancerName) studio.dancers.add(dancerName);
                        studio.competitions.add(compName);
                        studio.totalResults++;
                        
                        if (studio.sampleCompetitions.length < 3) {
                          studio.sampleCompetitions.push(compName);
                        }
                      }
                    });
                  }
                });
              }
            } catch (error) {
              // No dance-specific competitions
            }
          }
        }
      } catch (error) {
        console.log("No new structure found, using only old structure");
      }
      
      // Convert to array and sort by total results
      const studiosArray = Array.from(studiosMap.values()).map(studio => ({
        ...studio,
        dancers: Array.from(studio.dancers),
        competitions: Array.from(studio.competitions)
      })).sort((a, b) => b.totalResults - a.totalResults);
      
      setAllStudios(studiosArray);
      
      // Find potential duplicates
      const duplicates = findPotentialDuplicates(studiosArray);
      setPotentialDuplicates(duplicates);
      
      setMergeStatus(`✅ Loaded ${studiosArray.length} unique studios. Found ${duplicates.length} potential duplicate groups.`);
      
    } catch (error) {
      console.error("❌ Error loading studios:", error);
      setMergeStatus("❌ Error loading studios: " + error.message);
    }
    
    setLoading(false);
  }

  // 🔍 Find potential duplicates based on name similarity
  function findPotentialDuplicates(studios) {
    const duplicateGroups = [];
    const processed = new Set();
    
    for (let i = 0; i < studios.length; i++) {
      if (processed.has(i)) continue;
      
      const studio1 = studios[i];
      const group = [{
        ...studio1,
        similarity: 100 // The studio is 100% similar to themselves
      }];
      
      processed.add(i);
      
      for (let j = i + 1; j < studios.length; j++) {
        if (processed.has(j)) continue;
        
        const studio2 = studios[j];
        
        if (areStudiosSimilar(studio1.name, studio2.name)) {
          const similarity = calculateSimilarity(studio1.name, studio2.name);
          group.push({
            ...studio2,
            similarity: similarity
          });
          processed.add(j);
        }
      }
      
      if (group.length > 1) {
        duplicateGroups.push(group);
      }
    }
    
    return duplicateGroups;
  }

  // 🎯 Handle studio selection for merging
  function handleStudioSelect(studio) {
    setSelectedStudios(prev => {
      const isSelected = prev.some(s => s.name === studio.name);
      if (isSelected) {
        return prev.filter(s => s.name !== studio.name);
      } else {
        return [...prev, studio];
      }
    });
  }

  // 🔀 Merge selected studios
  async function mergeStudios() {
    if (selectedStudios.length < 2) {
      setMergeStatus("❌ Please select at least 2 studios to merge.");
      return;
    }
    
    const newName = prompt(`Enter the name for the merged studio (merging ${selectedStudios.length} studios):`, selectedStudios[0].name);
    if (!newName || newName.trim() === '') {
      setMergeStatus("❌ Merge cancelled.");
      return;
    }
    
    setMergeStatus(`🔄 Merging ${selectedStudios.length} studios into "${newName}"...`);
    
    try {
      const batch = writeBatch(db);
      let updatedCompetitions = 0;
      
      // Update old structure
      const oldQuery = collection(db, "competitions");
      const oldSnapshot = await getDocs(oldQuery);
      
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        let hasUpdates = false;
        
        if (comp.results) {
          comp.results.forEach((result) => {
            const matchingStudio = selectedStudios.find(s => s.name === result.studio);
            if (matchingStudio) {
              result.studio = newName.trim();
              hasUpdates = true;
            }
          });
        }
        
        if (hasUpdates) {
          batch.update(doc.ref, { results: comp.results });
          updatedCompetitions++;
        }
      });
      
      // Update new structure
      const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
      
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          
          // Update category competitions
          try {
            const categoryCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
            
            categoryCompsSnapshot.forEach((doc) => {
              const comp = doc.data();
              let hasUpdates = false;
              
              if (comp.results) {
                comp.results.forEach((result) => {
                  const matchingStudio = selectedStudios.find(s => s.name === result.studio);
                  if (matchingStudio) {
                    result.studio = newName.trim();
                    hasUpdates = true;
                  }
                });
              }
              
              if (hasUpdates) {
                batch.update(doc.ref, { results: comp.results });
                updatedCompetitions++;
              }
            });
          } catch (error) {
            // No direct competitions
          }
          
          // Update dance-specific competitions
          try {
            const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
            
            for (const danceDoc of dancesSnapshot.docs) {
              const danceName = danceDoc.id;
              const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
              
              danceCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                let hasUpdates = false;
                
                if (comp.results) {
                  comp.results.forEach((result) => {
                    const matchingStudio = selectedStudios.find(s => s.name === result.studio);
                    if (matchingStudio) {
                      result.studio = newName.trim();
                      hasUpdates = true;
                    }
                  });
                }
                
                if (hasUpdates) {
                  batch.update(doc.ref, { results: comp.results });
                  updatedCompetitions++;
                }
              });
            }
          } catch (error) {
            // No dance-specific competitions
          }
        }
      }
      
      // Commit all updates
      await batch.commit();
      
      setMergeStatus(`✅ Successfully merged ${selectedStudios.length} studios into "${newName}". Updated ${updatedCompetitions} competitions.`);
      setSelectedStudios([]);
      
      // Reload data
      setTimeout(() => {
        loadAllStudios();
      }, 2000);
      
    } catch (error) {
      console.error("❌ Error merging studios:", error);
      setMergeStatus("❌ Error merging studios: " + error.message);
    }
  }

  // 🔍 Filter studios based on search term and duplicate settings
  function getFilteredStudios() {
    let filtered = allStudios;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(studio => 
        studio.name.toLowerCase().includes(term) ||
        studio.dancers.some(dancer => dancer.toLowerCase().includes(term))
      );
    }
    
    if (showOnlyDuplicates) {
      const duplicateStudioNames = new Set();
      potentialDuplicates.forEach(group => {
        group.forEach(studio => duplicateStudioNames.add(studio.name));
      });
      filtered = filtered.filter(studio => duplicateStudioNames.has(studio.name));
    }
    
    return filtered;
  }

  if (loading) {
    return (
      <div className="merge-studios-container">
        <h2>🔄 Merge Studios</h2>
        <p>{mergeStatus}</p>
      </div>
    );
  }

  const filteredStudios = getFilteredStudios();

  return (
    <div className="merge-studios-container">
      <h2>🔄 Merge Dance Studios</h2>
      <p className="description">
        This tool helps you identify and merge duplicate dance studio profiles that may have been 
        created due to name variations (with/without "Dance Studio", capitalization, etc.). 
        Uses fuzzy matching to detect names with 70%+ similarity for misspellings and typos.
      </p>
      
      <div className="info-note" style={{ 
        backgroundColor: "#e3f2fd", 
        border: "1px solid #1976d2", 
        borderRadius: "4px", 
        padding: "12px", 
        margin: "16px 0",
        fontSize: "14px",
        color: "#1565c0"
      }}>
        <strong>📝 Note:</strong> When no representation, it is called "No Club"
      </div>
      
      {mergeStatus && (
        <div className={`status-message ${mergeStatus.startsWith('❌') ? 'error' : mergeStatus.startsWith('✅') ? 'success' : 'info'}`}>
          {mergeStatus}
        </div>
      )}
      
      <div className="controls">
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search by studio name or dancer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showOnlyDuplicates}
              onChange={(e) => setShowOnlyDuplicates(e.target.checked)}
            />
            Show only potential duplicates
          </label>
        </div>
        
        <div className="merge-controls">
          <p>Selected for merge: {selectedStudios.length} studios</p>
          {selectedStudios.length > 1 && (
            <button 
              onClick={mergeStudios}
              className="merge-button"
              disabled={!user}
            >
              Merge Selected Studios
            </button>
          )}
          {selectedStudios.length > 0 && (
            <button 
              onClick={() => setSelectedStudios([])}
              className="clear-button"
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>
      
      {!user && (
        <div className="warning">
          ⚠️ You must be logged in to perform merge operations.
        </div>
      )}
      
      <div className="stats">
        <p>
          📊 Total unique studios: {allStudios.length} | 
          Potential duplicate groups: {potentialDuplicates.length} | 
          Showing: {filteredStudios.length} studios
        </p>
      </div>
      
      <div className="studios-grid">
        {showOnlyDuplicates && potentialDuplicates.length > 0 ? (
          potentialDuplicates.map((group, groupIndex) => (
            <div key={groupIndex} className="duplicate-group">
              <h4>Potential Duplicate Group {groupIndex + 1}</h4>
              <div className="group-studios">
                {group.map((studio, index) => (
                  <StudioCard 
                    key={`${groupIndex}-${index}`}
                    studio={studio}
                    isSelected={selectedStudios.some(s => s.name === studio.name)}
                    onSelect={() => handleStudioSelect(studio)}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="studios-list">
            {filteredStudios.map((studio, index) => (
              <StudioCard 
                key={index}
                studio={studio}
                isSelected={selectedStudios.some(s => s.name === studio.name)}
                onSelect={() => handleStudioSelect(studio)}
              />
            ))}
          </div>
        )}
      </div>
      
      {filteredStudios.length === 0 && (
        <div className="no-results">
          {showOnlyDuplicates 
            ? "🎉 No potential duplicates found!" 
            : "No studios match your search criteria."}
        </div>
      )}
    </div>
  );
}

// 🏢 Individual studio card component
function StudioCard({ studio, isSelected, onSelect }) {
  return (
    <div 
      className={`studio-card ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
    >
      <div className="studio-header">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div className="studio-icon">🏢</div>
          <h4>{studio.name}</h4>
        </div>
        <div className="header-info">
          {studio.similarity !== undefined && studio.similarity < 100 && (
            <div className="similarity-badge" title={`${studio.similarity}% similar to group reference`}>
              {studio.similarity}% match
            </div>
          )}
          <div className="selection-indicator">
            {isSelected ? '✅' : '⬜'}
          </div>
        </div>
      </div>
      
      <div className="studio-details">
        <p><strong>Dancers:</strong> {studio.dancers.length}</p>
        <p><strong>Competitions:</strong> {studio.competitions.length}</p>
        <p><strong>Total Results:</strong> {studio.totalResults}</p>
        
        {studio.dancers.length > 0 && (
          <div className="sample-dancers">
            <strong>Sample dancers:</strong>
            <ul>
              {studio.dancers.slice(0, 3).map((dancer, index) => (
                <li key={index}>{dancer}</li>
              ))}
              {studio.dancers.length > 3 && (
                <li>... and {studio.dancers.length - 3} more</li>
              )}
            </ul>
          </div>
        )}
        
        {studio.sampleCompetitions.length > 0 && (
          <div className="sample-competitions">
            <strong>Recent competitions:</strong>
            <ul>
              {studio.sampleCompetitions.map((comp, index) => (
                <li key={index}>{comp}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
